<script lang="ts">
  import Modal from './Modal.svelte'
  import { createEventDispatcher } from 'svelte'
  import { addToast } from '../../lib/toast'

  export let isOpen = false
  export let token = ''

  const dispatch = createEventDispatcher()

  // Tab state
  let activeTab: 'full' | 'simple' = 'full'

  // Full test tab
  let tenant = ''
  let domain = ''
  // URLs dinámicas basadas en el dominio actual
  let wsUrl = ''
  let broadcastUrl = ''
  $: {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const httpProto = location.protocol === 'https:' ? 'https' : 'http'
    const host = (import.meta as any).env?.DEV ? `${location.hostname}:8060` : location.host
    wsUrl = `${proto}://${host}/api/v1/ws`
    broadcastUrl = `${httpProto}://${host}/api/v1/broadcast`
  }
  
  // WebSocket state for full test
  let ws: WebSocket | null = null
  let connected = false
  let messages: Array<{type: string, data: any, timestamp: string}> = []
  
  // Broadcast form
  let eventName = 'test_event'
  let eventData = '{"message": "Hello from test!"}'
  let sending = false

  // Simple test tab
  let simpleWsUrl = ''
  $: {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws'
    const host = (import.meta as any).env?.DEV ? `${location.hostname}:8060` : location.host
    simpleWsUrl = `${proto}://${host}/api/v1/ws`
  }
  let simpleWs: WebSocket | null = null
  let simpleConnected = false
  let simpleMessages: Array<{type: string, data: any, timestamp: string}> = []

  function connect() {
    if (ws) {
      addToast('Ya hay una conexión activa', 'info')
      return
    }

    if (!token || !tenant || !domain) {
      addToast('Por favor completa todos los campos', 'error')
      return
    }

    try {
      const url = `${wsUrl}?domain=${encodeURIComponent(domain)}&tenant=${encodeURIComponent(tenant)}&token=${encodeURIComponent(token)}`
      ws = new WebSocket(url)

      ws.onopen = () => {
        connected = true
        addToast('Conectado al WebSocket', 'success')
        addMessage('system', { status: 'connected' })
      }

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          addMessage('received', msg)
          
          if (msg.type === 'subscribed') {
            addToast(`Suscrito a: ${msg.data?.channel}`, 'success')
          }
        } catch (e) {
          addMessage('error', { error: 'Failed to parse message', raw: event.data })
        }
      }

      ws.onerror = (error) => {
        addMessage('error', { error: 'WebSocket error', details: error })
        addToast('Error en la conexión WebSocket', 'error')
      }

      ws.onclose = () => {
        connected = false
        ws = null
        addMessage('system', { status: 'disconnected' })
        addToast('Desconectado del WebSocket', 'info')
      }
    } catch (e: any) {
      addToast('Error al conectar: ' + e.message, 'error')
    }
  }

  function disconnect() {
    if (ws) {
      ws.close()
      ws = null
      connected = false
    }
  }

  function addMessage(type: string, data: any) {
    messages = [...messages, {
      type,
      data,
      timestamp: new Date().toLocaleTimeString()
    }]
  }

  // Simple connection functions
  function simpleConnect() {
    if (simpleWs) {
      addToast('Ya hay una conexión activa', 'info')
      return
    }

    if (!token) {
      addToast('Por favor ingresa el token', 'error')
      return
    }

    try {
      const url = `${simpleWsUrl}?token=${encodeURIComponent(token)}`
      simpleWs = new WebSocket(url)

      simpleWs.onopen = () => {
        simpleConnected = true
        addToast('Conectado al WebSocket (Simple)', 'success')
        addSimpleMessage('system', { status: 'connected' })
      }

      simpleWs.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data)
          addSimpleMessage('received', msg)
        } catch (e) {
          addSimpleMessage('error', { error: 'Failed to parse message', raw: event.data })
        }
      }

      simpleWs.onerror = (error) => {
        addSimpleMessage('error', { error: 'WebSocket error', details: error })
        addToast('Error en la conexión WebSocket', 'error')
      }

      simpleWs.onclose = () => {
        simpleConnected = false
        simpleWs = null
        addSimpleMessage('system', { status: 'disconnected' })
        addToast('Desconectado del WebSocket', 'info')
      }
    } catch (e: any) {
      addToast('Error al conectar: ' + e.message, 'error')
    }
  }

  function simpleDisconnect() {
    if (simpleWs) {
      simpleWs.close()
      simpleWs = null
      simpleConnected = false
    }
  }

  function addSimpleMessage(type: string, data: any) {
    simpleMessages = [...simpleMessages, {
      type,
      data,
      timestamp: new Date().toLocaleTimeString()
    }]
  }

  async function sendBroadcast() {
    if (!token || !tenant || !domain) {
      addToast('Por favor completa todos los campos', 'error')
      return
    }

    let parsedData
    try {
      parsedData = JSON.parse(eventData)
    } catch (e) {
      addToast('El JSON del evento no es válido', 'error')
      return
    }

    sending = true
    try {
      const response = await fetch(broadcastUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          domain,
          tenant,
          event: eventName,
          data: parsedData
        })
      })

      const result = await response.json()
      
      if (response.ok) {
        addToast(`Broadcast enviado a ${result.sent_to || 0} clientes`, 'success')
        addMessage('broadcast_sent', result)
      } else {
        addToast('Error al enviar broadcast: ' + (result.error || 'Unknown'), 'error')
      }
    } catch (e: any) {
      addToast('Error al enviar broadcast: ' + e.message, 'error')
    } finally {
      sending = false
    }
  }

  function clearMessages() {
    messages = []
  }

  function clearSimpleMessages() {
    simpleMessages = []
  }

  function close() {
    disconnect()
    simpleDisconnect()
    dispatch('close')
  }
</script>

<Modal {isOpen} title="Prueba de WebSocket" maxWidth="max-w-6xl" on:close={close}>
  <!-- Tabs -->
  <div class="flex gap-2 mb-6 border-b border-white/10">
    <button
      on:click={() => activeTab = 'full'}
      class="px-4 py-2 text-sm font-bold transition-all {activeTab === 'full' ? 'text-white border-b-2 border-pink-500' : 'text-indigo-200/60 hover:text-white'}"
    >
      🔧 Prueba Completa
    </button>
    <button
      on:click={() => activeTab = 'simple'}
      class="px-4 py-2 text-sm font-bold transition-all {activeTab === 'simple' ? 'text-white border-b-2 border-pink-500' : 'text-indigo-200/60 hover:text-white'}"
    >
      ⚡ Prueba Simple
    </button>
  </div>

  {#if activeTab === 'full'}
    <!-- Full Test Tab -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[70vh]">
      <!-- Left Column: Controls -->
      <div class="space-y-6 overflow-y-auto pr-2">
        <!-- Connection Settings -->
        <div class="space-y-4">
          <h4 class="text-sm font-bold text-white uppercase tracking-wider">Configuración</h4>
          
          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Token</label>
            <input 
              bind:value={token}
              disabled={connected}
              class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs font-mono disabled:opacity-50"
              placeholder="Token de autenticación"
            />
          </div>
          
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Tenant</label>
              <input 
                bind:value={tenant}
                disabled={connected}
                class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs disabled:opacity-50"
                placeholder="ej: sunat"
              />
            </div>
            
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Dominio</label>
              <input 
                bind:value={domain}
                disabled={connected}
                class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs disabled:opacity-50"
                placeholder="ej: smart.oo"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">WebSocket URL</label>
            <input 
              bind:value={wsUrl}
              disabled={connected}
              class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs font-mono disabled:opacity-50"
              placeholder="ws://..."
            />
          </div>

          <!-- Connection Button -->
          <div class="flex gap-3 items-center">
            {#if !connected}
              <button 
                on:click={connect}
                class="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white rounded-lg font-bold shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm"
              >
                🔌 Conectar
              </button>
            {:else}
              <button 
                on:click={disconnect}
                class="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white rounded-lg font-bold shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm"
              >
                ⚡ Desconectar
              </button>
            {/if}
            
            <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/10">
              <div class="w-2 h-2 rounded-full {connected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}"></div>
              <span class="text-xs text-indigo-200/70">{connected ? 'Conectado' : 'Desconectado'}</span>
            </div>
          </div>
        </div>

        <!-- Broadcast Section -->
        <div class="border-t border-white/10 pt-4">
          <h4 class="text-sm font-bold text-white mb-4 uppercase tracking-wider">Enviar Broadcast</h4>
          
          <div class="space-y-3">
            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Broadcast URL</label>
              <input 
                bind:value={broadcastUrl}
                class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs font-mono"
                placeholder="http://..."
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Nombre del Evento</label>
              <input 
                bind:value={eventName}
                class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs"
                placeholder="ej: new_notification"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Datos (JSON)</label>
              <textarea 
                bind:value={eventData}
                rows="3"
                class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs font-mono resize-none"
              ></textarea>
            </div>

            <button 
              on:click={sendBroadcast}
              disabled={sending}
              class="w-full px-4 py-2 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 hover:from-pink-400 hover:via-purple-400 hover:to-indigo-400 text-white rounded-lg font-bold shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {sending ? '📤 Enviando...' : '📤 Enviar Broadcast'}
            </button>
          </div>
        </div>
      </div>

      <!-- Right Column: Messages Log -->
      <div class="flex flex-col h-full min-h-0">
        <div class="flex justify-between items-center mb-3">
          <h4 class="text-sm font-bold text-white uppercase tracking-wider">Mensajes ({messages.length})</h4>
          <button 
            on:click={clearMessages}
            class="px-2 py-1 text-xs text-indigo-200/60 hover:text-white transition-colors"
          >
            🗑️ Limpiar
          </button>
        </div>
        
        <div class="bg-black/30 rounded-xl border border-white/10 p-3 flex-1 overflow-y-auto space-y-2">
          {#if messages.length === 0}
            <p class="text-indigo-200/50 text-xs text-center py-8">No hay mensajes aún</p>
          {:else}
            {#each messages as msg}
              <div class="text-xs font-mono p-2 rounded bg-white/5 border-l-2 {msg.type === 'error' ? 'border-red-500' : msg.type === 'system' ? 'border-yellow-500' : msg.type === 'broadcast_sent' ? 'border-purple-500' : 'border-emerald-500'}">
                <div class="flex justify-between mb-1">
                  <span class="text-indigo-300 font-bold text-[10px]">{msg.type}</span>
                  <span class="text-indigo-200/50 text-[10px]">{msg.timestamp}</span>
                </div>
                <pre class="text-indigo-100/80 whitespace-pre-wrap break-all text-[10px]">{JSON.stringify(msg.data, null, 2)}</pre>
              </div>
            {/each}
          {/if}
        </div>
      </div>
    </div>
  {:else}
    <!-- Simple Test Tab -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 max-h-[70vh]">
      <!-- Left Column: Simple Controls -->
      <div class="space-y-6">
        <div class="space-y-4">
          <h4 class="text-sm font-bold text-white uppercase tracking-wider">Conexión Simple</h4>
          <p class="text-xs text-indigo-200/60">Solo requiere el token para conectar</p>
          
          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">Token</label>
            <input 
              bind:value={token}
              disabled={simpleConnected}
              class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs font-mono disabled:opacity-50"
              placeholder="Token de autenticación"
            />
          </div>

          <div>
            <label class="block text-xs font-bold text-indigo-200 uppercase tracking-wider mb-2">WebSocket URL</label>
            <input 
              bind:value={simpleWsUrl}
              disabled={simpleConnected}
              class="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 transition-all outline-none placeholder-white/20 text-xs font-mono disabled:opacity-50"
              placeholder="ws://..."
            />
          </div>

          <!-- Connection Button -->
          <div class="flex gap-3 items-center">
            {#if !simpleConnected}
              <button 
                on:click={simpleConnect}
                class="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white rounded-lg font-bold shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm"
              >
                🔌 Conectar
              </button>
            {:else}
              <button 
                on:click={simpleDisconnect}
                class="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white rounded-lg font-bold shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm"
              >
                ⚡ Desconectar
              </button>
            {/if}
            
            <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/10">
              <div class="w-2 h-2 rounded-full {simpleConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}"></div>
              <span class="text-xs text-indigo-200/70">{simpleConnected ? 'Conectado' : 'Desconectado'}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Simple Messages Log -->
      <div class="flex flex-col h-full min-h-0">
        <div class="flex justify-between items-center mb-3">
          <h4 class="text-sm font-bold text-white uppercase tracking-wider">Mensajes ({simpleMessages.length})</h4>
          <button 
            on:click={clearSimpleMessages}
            class="px-2 py-1 text-xs text-indigo-200/60 hover:text-white transition-colors"
          >
            🗑️ Limpiar
          </button>
        </div>
        
        <div class="bg-black/30 rounded-xl border border-white/10 p-3 flex-1 overflow-y-auto space-y-2">
          {#if simpleMessages.length === 0}
            <p class="text-indigo-200/50 text-xs text-center py-8">No hay mensajes aún</p>
          {:else}
            {#each simpleMessages as msg}
              <div class="text-xs font-mono p-2 rounded bg-white/5 border-l-2 {msg.type === 'error' ? 'border-red-500' : msg.type === 'system' ? 'border-yellow-500' : 'border-emerald-500'}">
                <div class="flex justify-between mb-1">
                  <span class="text-indigo-300 font-bold text-[10px]">{msg.type}</span>
                  <span class="text-indigo-200/50 text-[10px]">{msg.timestamp}</span>
                </div>
                <pre class="text-indigo-100/80 whitespace-pre-wrap break-all text-[10px]">{JSON.stringify(msg.data, null, 2)}</pre>
              </div>
            {/each}
          {/if}
        </div>
      </div>
    </div>
  {/if}

  <div class="flex justify-end gap-3 mt-4 pt-4 border-t border-white/10">
    <button 
      on:click={close}
      class="px-4 py-2 text-indigo-200/60 hover:text-white transition-colors font-medium text-sm"
    >
      Cerrar
    </button>
  </div>
</Modal>
